import { apiService } from './api'

// トークン使用量サマリーの型定義
export interface TokenUsageSummary {
  period: {
    month: string
    startDate: string
    endDate: string
  }
  summary: {
    totalTokens: number
    totalCredits: number
    totalCount: number
  }
  byType: {
    type: string
    tokens: number
    credits: number
    count: number
  }[]
  byTenant: {
    tenantId: string
    tenantName: string
    tenantSlug: string
    subscriptionId: string
    monthlyTokens: number
    monthlyCredits: number
    monthlyCount: number
    totalTokens: number
    totalCredits: number
  }[]
  monthlyTrend: {
    month: string
    tokens: number
  }[]
}

// テナント別トークン使用量詳細の型定義
export interface TenantTokenUsageDetail {
  tenant: {
    id: string
    name: string
    slug: string
  }
  period: {
    month: string
    startDate: string
    endDate: string
  } | null
  byType: {
    type: string
    tokens: number
    credits: number
    count: number
  }[]
  monthlyTrend: {
    month: string
    tokens: number
  }[]
  usages: {
    id: string
    usageType: string
    tokenCount: number | null
    creditsUsed: number
    contentLength: number | null
    description: string | null
    usedAt: string
    usedBy: string
  }[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export class BillingService {
  /**
   * トークン使用量サマリーを取得（SUPER_ADMIN専用）
   */
  async getTokenUsageSummary(month?: string): Promise<TokenUsageSummary> {
    try {
      const params = month ? { month } : {}
      const response = await apiService.get<{ success: boolean; data: TokenUsageSummary }>(
        '/billing/token-usage/summary',
        params
      )
      return response.data
    } catch (error) {
      console.error('Failed to get token usage summary:', error)
      throw error
    }
  }

  /**
   * 特定テナントのトークン使用量詳細を取得（SUPER_ADMIN専用）
   */
  async getTokenUsageByTenant(
    tenantId: string,
    options?: { month?: string; page?: number; limit?: number }
  ): Promise<TenantTokenUsageDetail> {
    try {
      const params: any = {}
      if (options?.month) params.month = options.month
      if (options?.page) params.page = options.page
      if (options?.limit) params.limit = options.limit

      const response = await apiService.get<{ success: boolean; data: TenantTokenUsageDetail }>(
        `/billing/token-usage/tenant/${tenantId}`,
        params
      )
      return response.data
    } catch (error) {
      console.error('Failed to get token usage by tenant:', error)
      throw error
    }
  }

  /**
   * トークン使用量をCSVエクスポート（SUPER_ADMIN専用）
   */
  async exportTokenUsage(options?: { month?: string; tenantId?: string }): Promise<void> {
    try {
      const params = new URLSearchParams()
      if (options?.month) params.append('month', options.month)
      if (options?.tenantId) params.append('tenantId', options.tenantId)

      const queryString = params.toString()
      const url = `/billing/token-usage/export${queryString ? `?${queryString}` : ''}`

      // Blobとしてダウンロード
      const blob = await apiService.downloadBlob(url)

      // ファイル名を生成
      const filename = `token-usage-${options?.month || 'all'}${options?.tenantId ? `-${options.tenantId}` : ''}.csv`

      // ダウンロードリンクを作成
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Failed to export token usage:', error)
      throw error
    }
  }

  /**
   * 月の選択肢を生成（過去12ヶ月）
   */
  getMonthOptions(): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = []
    const now = new Date()

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = `${date.getFullYear()}年${date.getMonth() + 1}月`
      options.push({ value, label })
    }

    return options
  }

  /**
   * 使用タイプのラベルを取得
   */
  getUsageTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      TEST_GENERATION: 'テスト生成',
      CONTENT_TRANSLATION: 'コンテンツ翻訳',
    }
    return labels[type] || type
  }

  /**
   * トークン数をフォーマット
   */
  formatTokenCount(count: number): string {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(2)}M`
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`
    }
    return count.toLocaleString()
  }
}

export const billingService = new BillingService()
